import React, { useMemo, useCallback } from 'react';
import { Button, Checkbox, Table, Tag, Icon } from 'antd';
import {
  UNKNOWN_HOST_GROUP_KEY,
  allowlistHostGroupKey,
  buildAllowlistSelectionDerived,
  minimumAllowlistSelectionIds,
  compareAllowlistRowsForDisplay
} from './pullPropertyAllowlistShared';

export { UNKNOWN_HOST_GROUP_KEY };

/**
 * Group property rows under mapped hosts (same order as Host Mappings when `extAccesses` is token detail).
 */
export function groupPullPropertyRowsByHost(propertyRows, extAccesses) {
  const accessMeta = new Map();
  let nextExtraOrder = 5000;
  (extAccesses || []).forEach((a, idx) => {
    const hid = a.hostId != null ? String(a.hostId) : '';
    if (!hid) return;
    if (!accessMeta.has(hid)) {
      accessMeta.set(hid, {
        hostName: a.hostName || 'Host',
        hostExternalId: a.externalId != null ? String(a.externalId) : null,
        order: idx
      });
    }
  });

  const byHost = new Map();
  (propertyRows || []).forEach(r => {
    const hid = allowlistHostGroupKey(r);
    if (!byHost.has(hid)) byHost.set(hid, []);
    byHost.get(hid).push(r);
  });

  byHost.forEach((rows, hid) => {
    if (hid === UNKNOWN_HOST_GROUP_KEY || accessMeta.has(hid)) return;
    const first = rows[0];
    accessMeta.set(hid, {
      hostName: (first && first.hostName) || `Host (${String(hid).slice(0, 8)}…)`,
      hostExternalId: first && first.hostExternalId != null ? first.hostExternalId : null,
      order: nextExtraOrder++
    });
  });

  const hostKeys = Array.from(byHost.keys()).sort((a, b) => {
    if (a === UNKNOWN_HOST_GROUP_KEY) return 1;
    if (b === UNKNOWN_HOST_GROUP_KEY) return -1;
    const ma = accessMeta.get(a);
    const mb = accessMeta.get(b);
    const oa = ma && ma.order != null ? ma.order : 10000;
    const ob = mb && mb.order != null ? mb.order : 10000;
    if (oa !== ob) return oa - ob;
    const na = ma ? ma.hostName : a;
    const nb = mb ? mb.hostName : b;
    return String(na).localeCompare(String(nb));
  });

  return hostKeys.map(hid => {
    const meta = accessMeta.get(hid) || {
      hostName: hid === UNKNOWN_HOST_GROUP_KEY ? 'Unknown host' : 'Host',
      hostExternalId: null,
      order: 99999
    };
    const rows = byHost.get(hid).slice();
    rows.sort(compareAllowlistRowsForDisplay);
    return {
      hostKey: hid,
      hostName: meta.hostName,
      hostExternalId: meta.hostExternalId,
      rows
    };
  });
}

function buildAllowlistColumns(selectedIdSet, showSyncStatus, toggleRow, isRowUncheckDisabled) {
  const columns = [
    {
      title: 'Include',
      key: 'include',
      width: 64,
      render: (_, record) => (
        <Checkbox
          checked={selectedIdSet.has(String(record.id))}
          disabled={selectedIdSet.has(String(record.id)) && isRowUncheckDisabled(record.id)}
          onChange={e => toggleRow(record.id, e.target.checked)}
        />
      )
    }
  ];

  if (showSyncStatus) {
    columns.push({
      title: 'Status',
      key: 'status',
      width: 128,
      render: (_, record) =>
        record.alreadyOnToken ? (
          <Tag color="green" style={{ margin: 0 }}>
            Synced
          </Tag>
        ) : (
          <Tag color="orange" style={{ margin: 0 }}>
            Unsynced
          </Tag>
        )
    });
  }

  columns.push(
    { title: 'Property', dataIndex: 'name', key: 'name' },
    {
      title: 'External ID',
      dataIndex: 'externalId',
      key: 'externalId',
      render: text => <Tag style={{ fontFamily: 'monospace', fontSize: 11 }}>{text}</Tag>
    }
  );

  return columns;
}

/**
 * Toolbar + checkbox table for synced-property allowlists.
 * “Select all” toggles: all selected → clear; otherwise → select all.
 */
export function SyncedPropertiesAllowlist(props) {
  const {
    propertyRows = [],
    selectedIds = [],
    onSelectedIdsChange,
    tableScrollY = 280,
    showSyncStatus: showSyncStatusProp,
    groupByHost: groupByHostProp,
    hostGroupsExtAccesses = [],
    enforceMinSelected = false,
    enforcePerHostMinSelected = false
  } = props;

  const showSyncStatus = !!showSyncStatusProp;
  const groupByHost = !!groupByHostProp;

  const derived = useMemo(() => {
    const allIds = propertyRows.map(r => r.id);
    const total = propertyRows.length;
    const { selectedIdSet, rowById, selectedCountByHost } = buildAllowlistSelectionDerived(propertyRows, selectedIds);
    const minimumIdsForClearAll = minimumAllowlistSelectionIds(propertyRows, {
      enforceMinSelected,
      enforcePerHostMinSelected
    });
    const hostGroups = groupByHost ? groupPullPropertyRowsByHost(propertyRows, hostGroupsExtAccesses) : null;
    const flatDataSource = hostGroups ? propertyRows : propertyRows.slice().sort(compareAllowlistRowsForDisplay);
    const allSelected =
      total > 0 && selectedIds.length === total && selectedIdSet.size === total && allIds.every(id => selectedIdSet.has(String(id)));
    return {
      allIds,
      total,
      selectedIdSet,
      rowById,
      selectedCountByHost,
      minimumIdsForClearAll,
      hostGroups,
      flatDataSource,
      allSelected
    };
  }, [propertyRows, selectedIds, groupByHost, hostGroupsExtAccesses, enforceMinSelected, enforcePerHostMinSelected]);

  const toggleSelectAll = useCallback(() => {
    if (derived.allSelected) {
      onSelectedIdsChange(derived.minimumIdsForClearAll.slice());
      return;
    }
    onSelectedIdsChange(derived.allIds.slice());
  }, [derived.allSelected, derived.allIds, derived.minimumIdsForClearAll, onSelectedIdsChange]);

  const toggleRow = useCallback(
    (id, checked) => {
      const idKey = String(id);
      const { selectedIdSet, rowById, selectedCountByHost } = derived;
      if (checked) {
        if (selectedIdSet.has(idKey)) return;
        onSelectedIdsChange(selectedIds.concat([id]));
        return;
      }
      const current = rowById.get(idKey);
      if (!current) return;
      const hostKey = allowlistHostGroupKey(current);
      if (enforcePerHostMinSelected && (selectedCountByHost.get(hostKey) || 0) <= 1) {
        return;
      }
      if (enforceMinSelected && selectedIds.length <= 1) {
        return;
      }
      onSelectedIdsChange(selectedIds.filter(x => String(x) !== idKey));
    },
    [derived, selectedIds, onSelectedIdsChange, enforceMinSelected, enforcePerHostMinSelected]
  );

  const isRowUncheckDisabled = useCallback(
    id => {
      const row = derived.rowById.get(String(id));
      if (!row) return false;
      const hostKey = allowlistHostGroupKey(row);
      if (enforcePerHostMinSelected && (derived.selectedCountByHost.get(hostKey) || 0) <= 1) return true;
      if (enforceMinSelected && selectedIds.length <= 1) return true;
      return false;
    },
    [derived, enforceMinSelected, enforcePerHostMinSelected, selectedIds.length]
  );

  const columns = useMemo(() => buildAllowlistColumns(derived.selectedIdSet, showSyncStatus, toggleRow, isRowUncheckDisabled), [
    derived.selectedIdSet,
    showSyncStatus,
    toggleRow,
    isRowUncheckDisabled
  ]);

  return (
    <React.Fragment>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Tag color="cyan">
          {selectedIds.length} / {derived.total} selected
        </Tag>
        <Button size="small" onClick={toggleSelectAll}>
          {derived.allSelected ? 'Clear all' : 'Select all'}
        </Button>
      </div>
      {derived.hostGroups ? (
        <div
          style={{
            maxHeight: tableScrollY,
            overflowY: 'auto',
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: '8px 8px 0'
          }}
        >
          {derived.hostGroups.map((g, gi) => (
            <div
              key={`${g.hostKey}_${gi}`}
              style={{
                marginBottom: gi < derived.hostGroups.length - 1 ? 16 : 8,
                paddingBottom: gi < derived.hostGroups.length - 1 ? 16 : 0,
                borderBottom: gi < derived.hostGroups.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.85)'
                }}
              >
                <Icon type="cloud-server" style={{ color: '#1890ff' }} />
                <span>{g.hostName}</span>
                {g.hostExternalId ? <Tag style={{ fontSize: 11, fontFamily: 'monospace', margin: 0 }}>{g.hostExternalId}</Tag> : null}
                <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>
                  {g.rows.length} {g.rows.length === 1 ? 'property' : 'properties'}
                </span>
              </div>
              <Table size="small" rowKey="id" pagination={false} dataSource={g.rows} columns={columns} />
            </div>
          ))}
        </div>
      ) : (
        <Table size="small" rowKey="id" pagination={false} scroll={{ y: tableScrollY }} dataSource={derived.flatDataSource} columns={columns} />
      )}
    </React.Fragment>
  );
}

export default SyncedPropertiesAllowlist;
